/**
 * @testrelic/playwright-analytics/api-fixture
 *
 * Standalone API-only fixture for pure API test suites.
 * Wraps only the Playwright `request` fixture with the API request tracker
 * and assertion tracker. Does NOT import any navigation or browser-related code.
 *
 * @example
 * import { test as base } from '\@playwright/test';
 * import { testRelicApiFixture } from '\@testrelic/playwright-analytics';
 * export const test = base.extend(testRelicApiFixture);
 */

import { expect as playwrightExpect } from '@playwright/test';
import { ApiRequestTracker } from './api-request-tracker.js';
import { AssertionTracker, createActiveAssertionAwareExpect, setActiveAssertionContext } from './assertion-tracker.js';
import { PAYLOAD_VERSION, ATTACHMENT_NAME, ATTACHMENT_CONTENT_TYPE } from '@testrelic/core';
import type { ConsoleLogEntry, TestRelicFilePayload } from '@testrelic/core';
import { JsonlFileWriter } from './jsonl-stream.js';
import { readApiConfig } from './fixture.js';

/**
 * Assertion-aware `expect` for API-only suites. Inside a test that ran through
 * this fixture, each assertion on an API response is recorded and linked to its
 * originating call so it surfaces in the cloud report's API workspace; outside
 * one it behaves exactly like Playwright's `expect`. Import THIS `expect` (not
 * the one from '@playwright/test') for API assertion capture.
 */
export const expect = createActiveAssertionAwareExpect(
  playwrightExpect as unknown as (...args: unknown[]) => unknown,
) as unknown as typeof playwrightExpect;

/**
 * Playwright-compatible fixture object for API-only test suites.
 * Provides only `request` (with API tracking and assertion capture). No browser dependency.
 */
export const testRelicApiFixture = {
  request: async (
    { request }: { request: import('@playwright/test').APIRequestContext },
    use: (request: import('@playwright/test').APIRequestContext) => Promise<void>,
    testInfo: import('@playwright/test').TestInfo,
  ): Promise<void> => {
    const apiConfig = readApiConfig(testInfo);

    if (!apiConfig.trackApiCalls) {
      await use(request);
      return;
    }

    const assertionTracker = new AssertionTracker();
    const apiTracker = new ApiRequestTracker(request, assertionTracker, apiConfig);
    apiTracker.intercept();
    // Route the exported assertion-aware `expect` to THIS test's tracker.
    setActiveAssertionContext(assertionTracker, apiTracker);

    // Capture terminal stdout/stderr during the test — stream to JSONL
    let consoleWriter: JsonlFileWriter | null = null;
    try { consoleWriter = new JsonlFileWriter('api-console'); } catch { /* graceful */ }
    let consoleLogCount = 0;
    const origStdoutWrite = process.stdout.write;
    const origStderrWrite = process.stderr.write;

    process.stdout.write = function (chunk: unknown, ...args: unknown[]): boolean {
      try {
        const text = typeof chunk === 'string' ? chunk : Buffer.isBuffer(chunk) ? chunk.toString('utf-8') : String(chunk);
        if (!text.startsWith('[testrelic]') && !text.startsWith('ℹ TestRelic') && !text.startsWith('✓ TestRelic') && !text.startsWith('⚠ TestRelic')) {
          if (consoleWriter) {
            consoleWriter.append({ level: 'stdout', text: text.replace(/\n$/, ''), timestamp: new Date().toISOString(), location: null } satisfies ConsoleLogEntry);
            consoleLogCount++;
          }
        }
      } catch { /* never crash */ }
      return origStdoutWrite.apply(process.stdout, [chunk, ...args] as never);
    } as typeof process.stdout.write;

    process.stderr.write = function (chunk: unknown, ...args: unknown[]): boolean {
      try {
        const text = typeof chunk === 'string' ? chunk : Buffer.isBuffer(chunk) ? chunk.toString('utf-8') : String(chunk);
        if (!text.startsWith('[testrelic]') && !text.startsWith('⚠ TestRelic')) {
          if (consoleWriter) {
            consoleWriter.append({ level: 'stderr', text: text.replace(/\n$/, ''), timestamp: new Date().toISOString(), location: null } satisfies ConsoleLogEntry);
            consoleLogCount++;
          }
        }
      } catch { /* never crash */ }
      return origStderrWrite.apply(process.stderr, [chunk, ...args] as never);
    } as typeof process.stderr.write;

    try {
      await use(request);
    } finally {
      process.stdout.write = origStdoutWrite;
      process.stderr.write = origStderrWrite;
      setActiveAssertionContext(null, null);
    }

    try {
      consoleWriter?.close();
      const { apiCallsFile, apiCallsCount, apiStatusMix } = apiTracker.getFileData();
      const apiAssertions = apiConfig.captureAssertions ? assertionTracker.getData() : [];
      const payload: TestRelicFilePayload = {
        testRelicData: true,
        version: PAYLOAD_VERSION as '2.0.0',
        navigations: [],
        apiAssertions,
        networkRequestsFile: null,
        networkRequestsCount: 0,
        consoleLogsFile: consoleLogCount > 0 ? consoleWriter?.getPath() ?? null : null,
        consoleLogsCount: consoleLogCount,
        apiCallsFile,
        apiCallsCount,
        apiStatusMix,
      };
      await testInfo.attach(ATTACHMENT_NAME, {
        body: Buffer.from(JSON.stringify(payload)),
        contentType: ATTACHMENT_CONTENT_TYPE,
      });
    } catch {
      // Graceful degradation: fixture without reporter is harmless
    }
    apiTracker.dispose();
    assertionTracker.dispose();
  },
};
